import React, { useContext, useEffect, useState, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { styled } from '@nextiva/next-ui-components';

import { LicensesTableHeader, LicenseList, PermissionNeeded } from 'components';
import Drawer from 'containers/Drawer';
import { EDIT_LICENSE_ASSIGNMENT } from 'constants/drawerTitles';
import { LICENSES } from 'constants/routes';
import { useSnackbar, useGetLicenseList } from 'hooks';

import { PurchaseLicenseModal } from 'components/PurchaseLicenseModal/PurchaseLicenseModal';
import { ConfirmationPage } from 'components/PurchaseLicenseModal/components/ConfirmationPage';
import EditAssignment from '../EditAssignment';
import { LicensesContext, actionTypes } from '../licensesContext';
import { normalizeLicense } from '../helpers';
import { filterLicensesByFeature } from '../../../../utils/license-helpers';
import { setNoPermission } from '../../../App/Page/duck/actions';

// NOTE: if there is no viewable area no table rows will render
const listViewArea = {
  height: 'calc(100vh - 300px)',
  overflowY: 'auto',
};

const StyledDiv = styled('div')`
  margin-top: 50px;
`;

const LicensesTabPanel = ({
  setNoPermission,
  history,
  match: { params },
  noPermissionLicense,
}) => {
  const {
    dispatch,
    licensesState: {
      licenseList,
      purchaseLicenseModalOpen,
      purchaseLicenseConfirmationPageOpen,
    },
  } = useContext(LicensesContext);

  const { data: licenses, isLoading, error } = useGetLicenseList();

  const [showDrawer, setShowDrawer] = useState(false);

  const setSnackbar = useSnackbar();

  const toggleDrawer = useCallback(() => {
    setShowDrawer(!showDrawer);
    if (showDrawer) {
      dispatch({
        type: actionTypes.RESET_EDIT_ASSIGNMENT_DRAWER,
      });
      history.push(LICENSES);
    }
  }, [showDrawer]);

  const getLicenses = () => {
    if (error) {
      if (error.status === 403) {
        setNoPermission(true);
        dispatch({
          type: actionTypes.SET_LICENSE_LIST_IS_LOADING,
          licenseListIsLoading: false,
        });
      }
      setSnackbar({
        message: `Couldn't get the list of licenses.`,
        severity: 'error',
      });
      return;
    }

    dispatch({
      type: actionTypes.SET_LICENSE_LIST,
      licenseList: normalizeLicense(licenses),
    });
    dispatch({
      type: actionTypes.SET_LICENSE_LIST_IS_LOADING,
      licenseListIsLoading: false,
    });
  };

  useEffect(() => {
    if (!licenseList || licenseList.length === 0) return;
    // filter out licenses with feature
    const filteredList = filterLicensesByFeature(licenseList, params.license);

    if (filteredList.length === 0) return;
    // find filtered license with the most available licenses
    const result = filteredList.reduce((state, ele) => {
      if (!state) return ele;
      if (
        (ele.number_of_licenses_available || ele.numberOfLicensesAvailable) >
        (state.number_of_licenses_available || ele.numberOfLicensesAvailable)
      ) {
        return ele;
      }

      return state;
    });

    // set license in context
    dispatch({
      type: actionTypes.SET_EDIT_LICENSE_ASSIGNMENT,
      license: result,
    });

    // open drawer
    toggleDrawer();
  }, [licenseList]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    getLicenses();
  }, [isLoading, licenses]);

  if (noPermissionLicense) {
    return (
      <StyledDiv>
        <PermissionNeeded />
      </StyledDiv>
    );
  }

  return (
    <>
      <LicensesTableHeader />
      <div style={listViewArea}>
        <LicenseList toggleDrawer={toggleDrawer} />
      </div>
      <Drawer
        name={EDIT_LICENSE_ASSIGNMENT}
        title={EDIT_LICENSE_ASSIGNMENT}
        toggleDrawer={toggleDrawer}
        open={showDrawer}
      >
        <EditAssignment toggleDrawer={toggleDrawer} />
      </Drawer>
      {purchaseLicenseModalOpen && <PurchaseLicenseModal />}
      {purchaseLicenseConfirmationPageOpen && <ConfirmationPage />}
    </>
  );
};

const mapStateToProps = (state) => ({
  noPermissionLicense: state.pageMeta?.noPermissionLicense,
});

const mapDispatchToProps = {
  setNoPermission,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(LicensesTabPanel));
